import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { ShareModule } from '../../share.module';
import { FilterCriteriaDataService } from '../services/filter-criteria-data.service';
import { DataService } from '../../services/data.service';
import {
  ConditionGroup,
  QueryCondition,
} from '../../../models/filter-group.model';
import {
  ALL_FIELDS,
  BUSINESS_LINE_OPTIONS,
  CONTRACT_TYPE,
  DATE_FIELDS,
  FIELD_ALIASES,
  OPERATORS,
  SINGLE_VALUE_FIELDS,
  TEXT_FIELDS,
  VAS_TYPE_OPTIONS,
} from '../../constants/filter-criteria.constants';
import { FormControl } from '@angular/forms';
import {
  CdkDragDrop,
  CdkDragStart,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  isValidDrop,
  isValidTargetPlacement,
  moveAcrossGroups,
  moveItemsInArray,
} from '../moveFilterCriteria';
import { Notification, NotificationComponent } from '../../notification/notification.component';
import { SharedService } from '../../services/shared.service';
import { debounceTime, Subscription } from 'rxjs';
import { Reference } from '../../../models/reference';
import { SearchService } from '../../services/search.service';
import { AutoCompleteComponent } from '../../shared/auto-complete/auto-complete.component';
import { FilterCriteriaService } from '../../services/filter-criteria.service';
import { readFirstExcelColumn } from '../../shared/utils/excel-file.util';
import { FilterCriteriaGroupNotes } from './filter-criteria-group-notes/filter-criteria-group-notes';
import { NoteService } from '../../services/note.service';
@Component({
  selector: 'app-filter-criteria-group',
  standalone: true,
  imports: [CommonModule, ShareModule, DragDropModule, AutoCompleteComponent, NotificationComponent, FilterCriteriaGroupNotes],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './filter-criteria-group.component.html',
  styleUrl: './filter-criteria-group.component.scss',
})
export class FilterCriteriaGroupComponent implements OnChanges, OnDestroy {
  @Input() isModal: boolean = false;
  @Input() isLoaded: boolean = false;
  @Input() currentRangeCondition: QueryCondition | null = null;
  @Input() showRangeDialog: boolean = false;
  @Output() openSingleValueRequested = new EventEmitter<QueryCondition>();

  selectedBusinessLines: string[] = [];
  notification: Notification = {} as Notification;
  sortedFields: { label: string; value: string }[] = [];
  isDragging: boolean = false;
  conditionsToBeMoved: QueryCondition[] = [];
  ivkModalData: {
    matNo: string;
    originalMatNo: string;
    ivkDescription: string;
    type: 'Included' | 'Excluded' | 'Error';
    isExcluded: boolean;
    isActive: boolean;
  }[] = [];
  showIvkModal: boolean = false;
  activeIvkCondId: string | null = null;
  ignoredIvkErrorsMap: Record<string, string[]> = {};
  activeRowMenu: number | null = null;

  showDeleteGroupModal: boolean = false;
  isDeletingGroup: boolean = false;
  groupIndexToDelete: number | null = null;

  @ViewChildren(FilterCriteriaGroupNotes)
  noteComponents!: QueryList<FilterCriteriaGroupNotes>;

  private selectedColumnsSubscription?: Subscription;

  constructor(
    public filterCriteriaDataService: FilterCriteriaDataService,
    public dataService: DataService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private sharedService: SharedService,
    private http: SearchService,
    private readonly filterCriteriaAPIService: FilterCriteriaService,
    private readonly noteService: NoteService,
  ) {}

  ngOnChanges(simpleChange: SimpleChanges) {
    if (simpleChange['isLoaded'] && simpleChange['isLoaded'].currentValue) {
      this.sortedFields = this.sortFieldsLabel(
        this.filterCriteriaDataService.fields,
      );
      this.filterCriteriaDataService.filteredFields = this.sortFieldsLabel(
        this.filterCriteriaDataService.fields,
      );
      this.selectedColumnsSubscription =
        this.sharedService.selectedColumns$.subscribe((columns) => {
          this.filterCriteriaDataService.filteredFields =
            this.getFieldsForSelectedColumns(columns);
        });

      this.getBusinessLines();
      this.getVasTypes();
      this.getContractTypes();
      this.getCountries();
    }
  }
  toggleGroupCollapse(groupIndex: number): void {
    if (this.filterCriteriaDataService.groups[groupIndex]) {
      this.filterCriteriaDataService.groups[groupIndex].collapsed =
        !this.filterCriteriaDataService.groups[groupIndex].collapsed;
      this.filterCriteriaDataService.groups[groupIndex].isExpanded =
        !this.filterCriteriaDataService.groups[groupIndex].isExpanded;
    }
  }

  openDeleteGroupConfirm(groupIndex: number): void {
    this.groupIndexToDelete = groupIndex;
    this.showDeleteGroupModal = true;
  }

  cancelDeleteGroup(): void {
    this.showDeleteGroupModal = false;
    this.groupIndexToDelete = null;
    this.isDeletingGroup = false;
  }

  confirmDeleteGroup(): void {
    if (this.groupIndexToDelete === null || this.isDeletingGroup) {
      return;
    }

    const groupIndex = this.groupIndexToDelete;
    const filterGuid = this.dataService.selectedGUID;
    const groupId =
      this.filterCriteriaDataService.groups[groupIndex]?.id || String(groupIndex);

    if (!filterGuid) {
      this.removeGroup(groupIndex);
      this.cancelDeleteGroup();
      return;
    }

    this.isDeletingGroup = true;
    this.noteService.deleteGroupNotes(filterGuid, groupId).subscribe({
      next: () => {
        this.removeGroup(groupIndex);
        // Refresh note components to clear the note count and hide the comment icon
        this.cdr.detectChanges();
        this.noteComponents?.forEach((noteComponent) =>
          noteComponent.loadNoteCount(),
        );
        this.cancelDeleteGroup();
      },
      error: () => {
        this.isDeletingGroup = false;
      },
    });
  }

  removeGroup(groupIndex: number): void {
    const group = this.filterCriteriaDataService.groups[groupIndex];
    if (!group) {
      return;
    }

    // Clean up excluded values map for all conditions in this group
    group.conditions.forEach((cond) => {
      delete this.filterCriteriaDataService.excludedValuesMap[cond.id];
    });

    if (this.filterCriteriaDataService.groups.length > 1) {
      // If there are multiple groups, delete the entire group
      this.filterCriteriaDataService.groups.splice(groupIndex, 1);
      this.connectedDropLists.splice(groupIndex, 1);
    } else {
      // If this is the only group, clear all field values from the conditions but keep the structure
      group.conditions.forEach((cond) => {
        cond.field = '';
        cond.operator = '';
        cond.value = '';
        cond.valueTo = '';
        if (cond.servicePartnerControl) {
          cond.servicePartnerControl.setValue('');
        }
      });
    }

    this.filterCriteriaDataService.updateContractOptions();
    this.filterCriteriaDataService.updateScrollAreaHeight();
    // Trigger change detection to update DOM elements and ensure note components are cleaned up
    this.cdr.detectChanges();
  }

  /**
   * Duplicates a group's filter criteria (metadata, logic and conditions) into a
   * new group placed directly below the source group. Group-level notes are NOT
   * copied - the duplicated group starts with an empty notes section.
   */
  duplicateGroupWithoutNotes(groupIndex: number): void {
    const groups = this.filterCriteriaDataService.groups;
    const source = groups[groupIndex];
    if (!source) {
      return;
    }

    const newGroup: ConditionGroup = {
      logic: source.logic,
      conditions: this.cloneGroupConditions(source),
      collapsed: false,
      isExpanded: true,
      name: this.getDuplicateGroupName(source.name),
      // Fresh stable id so the copy owns an independent (empty) notes section.
      id: this.filterCriteriaDataService.generateGroupId(),
    };

    const insertAt = groupIndex + 1;
    groups.splice(insertAt, 0, newGroup);

    this.filterCriteriaDataService.updateContractOptions();
    this.filterCriteriaDataService.updateScrollAreaHeight();

    // Re-render so the new note component exists, then realign the note badges
    // to their current group positions (the duplicated group has no notes).
    this.cdr.detectChanges();
    this.noteComponents?.forEach((noteComponent) =>
      noteComponent.loadNoteCount(),
    );

    this.filterCriteriaDataService.scrollToConditionRow(
      insertAt,
      0,
      this.elementRef,
    );

    this.notification = {
      label: 'Success',
      description: 'Group duplicated without notes.',
      type: 'success',
    };
  }

  /**
   * Deep-copies the conditions of a group, assigning fresh condition ids and
   * carrying over the associated IVK included/excluded value maps so the copy
   * is independent from the source.
   */
  private cloneGroupConditions(source: ConditionGroup): QueryCondition[] {
    const fcds = this.filterCriteriaDataService;
    return source.conditions.map((cond) => {
      const newId = fcds.generateConditionId();

      const excludedValue = fcds.excludedValuesMap[cond.id];
      if (excludedValue !== undefined) {
        fcds.excludedValuesMap[newId] = excludedValue;
      }

      const includedIvk = fcds.ivkMap[cond.id.toString()];
      if (includedIvk !== undefined) {
        fcds.ivkMap[newId.toString()] = includedIvk.map((item) => ({
          ...item,
        }));
      }

      return {
        ...cond,
        id: newId,
        value: Array.isArray(cond.value) ? [...cond.value] : cond.value,
        valueTo: Array.isArray(cond.valueTo) ? [...cond.valueTo] : cond.valueTo,
        servicePartnerControl: new FormControl(
          cond.servicePartnerControl?.value ?? '',
        ),
      } as QueryCondition;
    });
  }

  /**
   * Builds a distinguishable name for a duplicated group by suffixing "(Copy)",
   * ensuring uniqueness against the existing group names.
   */
  private getDuplicateGroupName(sourceName?: string): string {
    const base = (sourceName ?? '').trim();
    const existingNames = new Set(
      this.filterCriteriaDataService.groups.map((group) =>
        (group.name ?? '').trim(),
      ),
    );

    let candidate = base ? `${base} (Copy)` : '(Copy)';
    let counter = 2;
    while (existingNames.has(candidate)) {
      candidate = base ? `${base} (Copy ${counter})` : `(Copy ${counter})`;
      counter++;
    }
    return candidate;
  }

  addRow(groupIndex: number): void {
    const group = this.filterCriteriaDataService.groups[groupIndex];
    if (group) {
      const newCondition: QueryCondition = {
        id: this.filterCriteriaDataService.generateConditionId(),
        field: '',
        operator: OPERATORS.EQUAL,
        value: '',
        logic: 'AND',
        servicePartnerControl: new FormControl(''),
        depth: 0,
      };
      group.conditions.push(newCondition);
    }

    this.connectedDropLists.push(`group-drop-${groupIndex}`);
    this.filterCriteriaDataService.updateScrollAreaHeight();
    this.filterCriteriaDataService.scrollToConditionRow(
      groupIndex,
      this.filterCriteriaDataService.groups[groupIndex].conditions.length - 1,
      this.elementRef,
    );
  }

  onDragStarted(
    event: CdkDragStart,
    groupIndex: number,
    condIndex: number,
  ): void {
    this.isDragging = true;

    const list = this.filterCriteriaDataService.groups[groupIndex].conditions;
    const parentDepth = list[condIndex].depth ?? 0;

    this.conditionsToBeMoved = [list[condIndex]];

    for (let i = condIndex + 1; i < list.length; i++) {
      const item = list[i];
      if (item.depth && item.depth > parentDepth) {
        this.conditionsToBeMoved.push(item);
      } else {
        break;
      }
    }
  }

  drop(event: CdkDragDrop<QueryCondition[]>, targetGroupIndex: number) {
    this.notification = {} as Notification;
    this.isDragging = false;
    const sourceGroupIndex =
      event.previousContainer === event.container
        ? targetGroupIndex
        : event.item.data.groupIndex;

    const sourceList =
      this.filterCriteriaDataService.groups[sourceGroupIndex].conditions;
    const targetList =
      this.filterCriteriaDataService.groups[targetGroupIndex].conditions;

    const fromIndex = event.previousIndex;
    const toIndex = event.currentIndex;
    if (sourceGroupIndex === targetGroupIndex) {
      const sourceValidation = isValidDrop(sourceList, fromIndex, toIndex);
      if (!sourceValidation.valid) {
        if (sourceValidation.error) {
          setTimeout(() => {
            this.notification = {
              label: 'Error',
              description: sourceValidation.error ?? '',
              type: 'error',
            };
          });
        }
        this.conditionsToBeMoved = [];
        return;
      }
      moveItemsInArray(
        sourceList,
        fromIndex,
        toIndex,
        this.conditionsToBeMoved,
      );
    } else if (sourceGroupIndex !== targetGroupIndex) {
      const targetConditions = isValidTargetPlacement(
        targetList,
        sourceList,
        toIndex,
        fromIndex,
      );
      if (!targetConditions.valid) {
        setTimeout(() => {
          this.notification = {
            label: 'Error',
            description: targetConditions.error ?? '',
            type: 'error',
          };
        });
        this.conditionsToBeMoved = [];
        return;
      }
      moveAcrossGroups(
        this.filterCriteriaDataService.groups,
        sourceGroupIndex,
        targetGroupIndex,
        fromIndex,
        toIndex,
        this.conditionsToBeMoved,
      );
    }
    this.conditionsToBeMoved = [];
  }

  get connectedDropLists(): string[] {
    return this.filterCriteriaDataService.groups.map(
      (_, i) => `group-drop-${i}`,
    );
  }

  set connectedDropLists(value: string[]) {
    this.connectedDropLists.push(...value);
  }

  getRowPadding(condition: QueryCondition): number {
    const depth = condition.depth || 0;
    // Base padding for parent rows
    const basePadding = 44;
    // Additional padding per depth level for visual hierarchy
    const depthPadding = depth * 24;
    // Extra padding for child rows to compensate for smaller dropdown width
    // This aligns the field/operator/value dropdowns with parent row
    const dropdownCompensation = depth > 0 ? 22 : 0;
    return basePadding + depthPadding + dropdownCompensation;
  }

  onFieldChange(conditions: QueryCondition): void {
    conditions.value = '';
    conditions.valueTo = '';
    delete this.filterCriteriaDataService.combinationErrors[conditions.id];

    this.filterCriteriaDataService.showTextInput = true;
    this.filterCriteriaDataService.showDropdownfield = false;
    this.filterCriteriaDataService.showMultiselect = false;
    this.filterCriteriaDataService.showServicePartner = false; // Reset to text input
    this.filterCriteriaDataService.showDatePicker = false; // Reset date picker
    this.filterCriteriaDataService.showRangeDatePicker = false; // Reset range date picker
    // Clear search state
    if (conditions.field === 'Country') {
      conditions.value = [];
    }

    if (conditions.field === 'Service Partner') {
      this.filterCriteriaDataService.partnerSearchTextMap[conditions.id] = '';
      this.filterCriteriaDataService.filteredPartnerMap[conditions.id] = [];
      conditions.servicePartnerControl?.setValue('', { emitEvent: false }); // Reset FormControl
    }

    if (conditions.field === 'Business Line') {
      this.selectedBusinessLines = [];
      conditions.value = [];
    }

    if (conditions.field === 'VAS Type') {
      conditions.value = [];
      this.resetVasLevelConditions();
      this.filterCriteriaDataService.updateVasLevelOptions(this.elementRef);
    }
    if (conditions.field === 'Contract Type') {
      conditions.value = [];
      this.filterCriteriaDataService.contractOptions = true;
      this.filterCriteriaDataService.ruContractOptions = true;
      this.filterCriteriaDataService.daContractOptions = true;
      this.filterCriteriaDataService.contractDateEnabled = true;
    }

    // Recalculate contract type flags whenever any field changes
    this.filterCriteriaDataService.updateContractOptions();

    if (conditions.field === 'VAS Level') {
      conditions.value = [];
    }

    if (conditions.field === 'EQ Status') {
      conditions.value = [];
    }

    if (conditions.field === 'EQ Substatus') {
      conditions.value = [];
    }
    if (this.filterCriteriaDataService.isMaterialNoField(conditions.field)) {
      const condId = conditions.id.toString();
      delete this.filterCriteriaDataService.ivkMap[condId];
      this.filterCriteriaDataService.ivkLoadingMap[condId] = false;
    }
  }

  displayFieldLabel = (value: string) => {
    const found = ALL_FIELDS.find((f) => f.value === value);
    return found ? found.label : value;
  };

  onOperatorChange(condition: QueryCondition): void {
    if (
      !this.filterCriteriaDataService.isDateRangeOperator(condition.operator)
    ) {
      condition.valueTo = '';
    }
    delete this.filterCriteriaDataService.combinationErrors[condition.id];
  }

  getOperatorsForField(field: string): string[] {
    const baseOperators = ['=', '!=', '<', '>'];
    if (this.isTextInputField(field)) {
      return [...baseOperators, 'combination'];
    }
    return baseOperators;
  }

  isOperatorDropdownDisabled(condition: QueryCondition): boolean {
    const symbol = this.filterCriteriaDataService.getOperatorSymbol(condition?.operator ?? '');
    return (
      this.dataService.isRolloutUpdated ||
      symbol === '=,!='
    );
  }

  getTextFieldPlaceholder(field: string, operator: string): string {
    if (!field) {
      return 'Enter Value';
    }

    if (this.isSingleValueOnlyField(field)) {
      return 'Enter a Single Value';
    }

    const sym = this.filterCriteriaDataService.getOperatorSymbol(operator);
    switch (sym) {
      case '=':
      case '!=':
        return 'Enter Value (comma-separated, ranges like 123-456, wildcard like %value%)';
      case '<':
      case '>':
        return 'Enter a Single Value';
      case 'combination':
        return 'Enter Value - (A,B,C)  - (A & B & C)  - (A&B) ,(C&D)';
      default:
        return 'Enter Value';
    }
  }

  private isSingleValueOnlyField(field: string): boolean {
    return SINGLE_VALUE_FIELDS.includes(field?.toLowerCase());
  }

  normalizeSeparators(value: string): string {
    return this.filterCriteriaDataService.normalizeSeparators(value);
  }

  onValuePaste(event: ClipboardEvent, cond: QueryCondition): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!pasted) {
      return;
    }
    event.preventDefault();
    const normalized = this.normalizeSeparators(pasted);
    const existing = (cond.value || '').trim();
    cond.value = existing ? this.normalizeSeparators(existing + ',' + normalized) : normalized;
    this.onCombinationValueChange(cond);
    if (cond.field.includes('Material Number')) {
      // call material ivk api
    }
  }

  onCombinationValueChange(cond: QueryCondition): void {
    const normalized = this.normalizeSeparators(cond.value);
    if (normalized !== cond.value) {
      setTimeout(() => { cond.value = normalized; }, 0);
    }
    cond.value = normalized;
    const sym = this.filterCriteriaDataService.getOperatorSymbol(cond.operator);
    if (sym === 'combination') {
      const error = this.validateCombinationValue(cond.value);
      if (error) {
        this.filterCriteriaDataService.combinationErrors[cond.id] = error;
      } else {
        delete this.filterCriteriaDataService.combinationErrors[cond.id];
      }
    } else {
      delete this.filterCriteriaDataService.combinationErrors[cond.id];
    }
  }

  validateCombinationValue(value: string): string | null {
    if (!value || value.trim() === '') {
      return null;
    }
    const invalidChars = value.replace(/[a-zA-Z0-9\s&,()]/g, '');
    if (invalidChars.length > 0) {
      const uniqueInvalid = [...new Set(invalidChars.split(''))].join(' ');
      return `Invalid character(s): ${uniqueInvalid}. Only letters, numbers, '&', ',', '(' and ')' are allowed.`;
    }
    return null;
  }

  getDateRangeError(condition: QueryCondition): string | null {
    if (
      this.isDateField(condition.field?.toLowerCase()) &&
      this.filterCriteriaDataService.isDateRangeOperator(condition.operator) &&
      condition.value &&
      condition.valueTo
    ) {
      const startDate = new Date(condition.value);
      const endDate = new Date(condition.valueTo);
      if (
        !Number.isNaN(startDate.getTime()) &&
        !Number.isNaN(endDate.getTime()) &&
        startDate > endDate
      ) {
        return 'Start date cannot be greater than end date';
      }
    }
    return null;
  }

  private isDateField(field: string): boolean {
    return DATE_FIELDS.includes(field);
  }

  onPartnerSelected(selectedId: any, conditions: QueryCondition): void {
    conditions.value = selectedId;
    const selected = this.filterCriteriaDataService.filteredPartnerMap[
      conditions.id
    ]?.find((p) => p.id === selectedId);
    this.filterCriteriaDataService.partnerSearchTextMap[conditions.id] =
      selected?.name ?? selectedId;
  }

  isTextInputField(field: string): boolean {
    return TEXT_FIELDS.includes(field?.toLowerCase());
  }

  private resetVasLevelConditions(): void {
    for (const group of this.filterCriteriaDataService.groups) {
      for (const cond of group.conditions) {
        if (cond.field === 'VAS Level') {
          cond.value = [];
        }
      }
    }
  }

  toTitleCase = (value: string): string => {
    if (!value || typeof value !== 'string') {
      return value;
    }
    return value.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  onIVKValueChange(cond: any): void {
    const condId = cond.id.toString();
    const matNos = [...new Set(this.splitMatNos(cond.value || ''))];

    this.filterCriteriaDataService.ivkLoadingMap[condId] = true;

    if (matNos.length === 0) {
      this.filterCriteriaDataService.ivkMap[condId] = [];
      this.filterCriteriaDataService.ivkLoadingMap[condId] = false;
      this.cdr.detectChanges();
      return;
    }

    this.filterCriteriaDataService.ivkSearch$.next({ condId, matNos });
  }

  getIVKInlineText(condId: string): string {
    const items = this.filterCriteriaDataService.ivkMap[condId] || [];
    if (items.length === 1) {
      return items[0].name;
    }
    return '';
  }

  showIVKInline(condId: string): boolean {
    const hasSingleResolvedIvk = (this.filterCriteriaDataService.ivkMap[condId] || []).length === 1;
    const conditionId = Number(condId);
    const condition = this.findConditionById(conditionId);
    const rawValue = typeof condition?.value === 'string' ? condition.value : '';
    return hasSingleResolvedIvk && this.getTotalEnteredIVKs(rawValue, conditionId) === 1;
  }

  hasMoreIVK(condId: string): boolean {
    return (this.filterCriteriaDataService.ivkMap[condId] || []).length > 0;
  }

  shouldShowIVKStatus(rawValue: string, conditionId: number): boolean {
    const condId = conditionId.toString();
    return (
      (this.filterCriteriaDataService.ivkMap[condId]?.length || 0) > 0 ||
      this.checkForInvalidIVKs(rawValue, conditionId) ||
      this.hasIgnoredIVKs(condId)
    );
  }

  shouldShowIVKInfoIcon(rawValue: string, conditionId: number): boolean {
    const condId = conditionId.toString();
    return !this.checkForInvalidIVKs(rawValue, conditionId) && (
      this.getTotalEnteredIVKs(rawValue, conditionId) > 1 ||
      this.hasMoreIVK(condId) ||
      this.hasIgnoredIVKs(condId)
    );
  }

  private getTotalEnteredIVKs(rawValue: string, conditionId: number): number {
    const excludedRaw = this.filterCriteriaDataService.excludedValuesMap[conditionId] || '';
    return new Set([
      ...this.splitMatNos(rawValue),
      ...this.splitMatNos(excludedRaw),
    ]).size;
  }

  openIVKModal(condId: string, rawValue: string): void {
    this.activeIvkCondId = condId;
    const results = this.filterCriteriaDataService.ivkMap[condId] || [];
    const inputMatNos = this.splitMatNos(rawValue);

    const condIdNum = Number(condId);
    const excludedRaw =
      this.filterCriteriaDataService.excludedValuesMap[condIdNum] || '';
    const excludedMatNos = this.splitMatNos(excludedRaw);

    // Build modal rows only from what is currently visible in the value input.
    const allMatNos = [...new Set(inputMatNos)];
    const ignored = new Set(this.ignoredIvkErrorsMap[condId] || []);

    this.ivkModalData = allMatNos.map((mat) => {
      const found = results.find((r) => r.id === mat);
      const isExcluded = excludedMatNos.includes(mat);
      const type: 'Included' | 'Excluded' | 'Error' =
        found || ignored.has(mat)
          ? isExcluded
            ? 'Excluded'
            : 'Included'
          : 'Error';
      return {
        matNo: mat,
        originalMatNo: mat,
        ivkDescription: found ? found.name : '-',
        type,
        isExcluded,
        isActive: false,
      };
    });

    this.sortIvkModalData();
    this.autoSelectNextErrorMaterial();

    this.showIvkModal = true;
  }

  removeCondition(groupIndex: number, conditionIndex: number) {
    const group = this.filterCriteriaDataService.groups[groupIndex];
    const conditionToRemove = group.conditions[conditionIndex];
    const parentDepth = conditionToRemove?.depth || 0;

    // Clean up IVK map for the condition being removed
    if (conditionToRemove) {
      delete this.filterCriteriaDataService.ivkMap[
        conditionToRemove.id.toString()
      ];
    }

    // Count how many children this condition has (conditions with higher depth immediately following)
    let removeCount = 1;
    for (let i = conditionIndex + 1; i < group.conditions.length; i++) {
      const childDepth = group.conditions[i].depth || 0;
      if (childDepth > parentDepth) {
        removeCount++;
      } else {
        break;
      }
    }

    // Check if removing these conditions will leave the group empty
    const willGroupBeEmpty = group.conditions.length - removeCount === 0;
    const isOnlyGroup = this.filterCriteriaDataService.groups.length === 1;

    if (willGroupBeEmpty) {
      if (isOnlyGroup) {
        // Reset to a single empty condition instead of removing the group
        group.conditions.forEach((cond) => {
          delete this.filterCriteriaDataService.excludedValuesMap[cond.id];
          delete this.filterCriteriaDataService.ivkMap[cond.id.toString()]; // clean up IVK map for all conditions
        });

        group.conditions = [
          {
            id: this.filterCriteriaDataService.generateConditionId(),
            field: '',
            operator: '',
            value: '',
            logic: 'AND',
            servicePartnerControl: new FormControl(''),
            depth: 0,
          },
        ];
      } else {
        // Remove the entire group since it will be empty
        group.conditions.forEach((cond) => {
          delete this.filterCriteriaDataService.excludedValuesMap[cond.id];
          delete this.filterCriteriaDataService.ivkMap[cond.id.toString()]; // clean up IVK map for all conditions
        });
        this.filterCriteriaDataService.groups.splice(groupIndex, 1);
      }
    } else {
      // Remove just the condition(s)
      for (let i = conditionIndex; i < conditionIndex + removeCount; i++) {
        if (group.conditions[i]) {
          delete this.filterCriteriaDataService.excludedValuesMap[
            group.conditions[i].id
          ];
          delete this.filterCriteriaDataService.ivkMap[
            group.conditions[i].id.toString()
          ]; // clean up IVK map
        }
      }

      group.conditions.splice(conditionIndex, removeCount);
    }

    this.filterCriteriaDataService.updateContractOptions();
    this.cdr.detectChanges();
  }

  canAddChild(condition: QueryCondition): boolean {
    const depth = condition.depth || 0;
    return depth < 2;
  }

  addCondition(groupIndex: number, logic: 'AND' | 'OR', afterIndex: number) {
    const group = this.filterCriteriaDataService.groups[groupIndex];

    if (logic === 'OR') {
      const parentDepth = group.conditions[afterIndex]?.depth || 0;

      if (parentDepth >= 2) {
        return;
      }

      const newCond: QueryCondition = {
        id: this.filterCriteriaDataService.generateConditionId(),
        field: '',
        operator: OPERATORS.EQUAL,
        value: '',
        logic: 'OR',
        servicePartnerControl: new FormControl(''),
        depth: parentDepth + 1,
      };

      let insertIndex = afterIndex + 1;
      while (
        insertIndex < group.conditions.length &&
        (group.conditions[insertIndex].depth || 0) > parentDepth
      ) {
        insertIndex++;
      }

      group.conditions.splice(insertIndex, 0, newCond);
      this.filterCriteriaDataService.scrollToConditionRow(
        groupIndex,
        insertIndex,
        this.elementRef,
      );
    } else if (logic === 'AND') {
      const newAndGroup: ConditionGroup = {
        logic: 'AND',
        conditions: [
          {
            id: this.filterCriteriaDataService.generateConditionId(),
            field: '',
            operator: OPERATORS.EQUAL,
            value: '',
            servicePartnerControl: new FormControl(''),
            depth: 0,
          },
        ],
      };

      let insertIndex = groupIndex + 1;
      for (
        let i = groupIndex + 1;
        i < this.filterCriteriaDataService.groups.length;
        i++
      ) {
        if (this.filterCriteriaDataService.groups[i].logic !== 'AND') break;
        insertIndex = i + 1;
      }

      this.filterCriteriaDataService.groups.splice(insertIndex, 0, newAndGroup);
      this.filterCriteriaDataService.scrollToConditionRow(
        insertIndex,
        0,
        this.elementRef,
      );
    }
    this.filterCriteriaDataService.updateScrollAreaHeight();
  }

  shouldShowMoreMenu(condition: QueryCondition): boolean {
    return (
      this.showUploadField(condition.field) || this.hasRangeValues(condition)
    );
  }
  showUploadField(field: string): boolean {
    return TEXT_FIELDS.includes(field?.toLowerCase());
  }

  hasRangeValues(conditions: QueryCondition): boolean {
    const sym = this.filterCriteriaDataService.getOperatorSymbol(
      conditions.operator,
    );
    const excludedValues =
      this.filterCriteriaDataService.excludedValuesMap[conditions.id] || '';
    const hasExcludedValues = excludedValues.trim() !== '';
    const hasIncludedValues = this.hasConditionValue(conditions.value);

    return (
      TEXT_FIELDS.includes(conditions.field?.toLowerCase()) &&
      (sym === '=' || (sym === '!=' && hasExcludedValues && !hasIncludedValues))
    );
  }

  private hasConditionValue(value: unknown): boolean {
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return Boolean(value);
  }

  toggleRowMenu(conditionId: number): void {
    this.activeRowMenu =
      this.activeRowMenu === conditionId ? null : conditionId;
  }

  closeRowMenu(): void {
    this.activeRowMenu = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.activeRowMenu !== null) {
      const isMoreIcon = target.closest('.more-trigger');
      const isPopover = target.closest('sh-popover');
      if (!isMoreIcon && !isPopover) {
        this.closeRowMenu();
      }
    }
  }

  openSingleValueModal(conditions: QueryCondition): void {
    this.openSingleValueRequested.emit(conditions);
  }

  private readonly excludedOnlyValueEdits = new Set<number>();

  getConditionDisplayValue(condition: QueryCondition): string {
    const includedValues = this.splitConditionValues(
      condition.value ? String(condition.value) : '',
    );
    const excludedValues = this.splitConditionValues(
      this.filterCriteriaDataService.excludedValuesMap[condition.id] || '',
    );

    if (includedValues.length > 0 && excludedValues.length > 0) {
      return [...new Set([...includedValues, ...excludedValues])].join(',');
    }

    if (includedValues.length > 0) {
      return includedValues.join(',');
    }

    if (excludedValues.length > 0) {
      return excludedValues.join(',');
    }

    return '';
  }

  onConditionDisplayValueChange(
    value: string,
    condition: QueryCondition,
  ): void {
    const normalizedValue = this.normalizeSeparators(value);
    const excludedValues =
      this.filterCriteriaDataService.excludedValuesMap[condition.id];

    const previousIncluded = this.splitConditionValues(
      typeof condition.value === 'string' ? condition.value : '',
    );
    const previousExcluded = this.splitConditionValues(excludedValues || '');
    const hasPreviousIncluded = previousIncluded.length > 0;
    const hasPreviousExcluded = previousExcluded.length > 0;

    const currentOperatorSymbol = this.filterCriteriaDataService.getOperatorSymbol(
      condition.operator,
    );

    if (currentOperatorSymbol === '=,!=' || (hasPreviousIncluded && hasPreviousExcluded)) {
      const editedValues = this.splitConditionValues(normalizedValue);
      const previousIncludedSet = new Set(previousIncluded);
      const previousExcludedSet = new Set(previousExcluded);

      const nextIncluded: string[] = [];
      const nextExcluded: string[] = [];

      for (const item of editedValues) {
        if (previousExcludedSet.has(item) && !previousIncludedSet.has(item)) {
          nextExcluded.push(item);
        } else {
          nextIncluded.push(item);
        }
      }

      condition.value = nextIncluded.join(',');
      this.filterCriteriaDataService.excludedValuesMap[condition.id] =
        nextExcluded.join(',');
      return;
    }

    if (condition.value) {
      this.excludedOnlyValueEdits.delete(condition.id);
      condition.value = normalizedValue;
      const included = this.splitConditionValues(condition.value);
      const excluded = this.splitConditionValues(
        this.filterCriteriaDataService.excludedValuesMap[condition.id] || '',
      );
      this.syncOperatorForIncludedExcluded(condition, included, excluded);
      return;
    }

    if (excludedValues?.trim() || this.excludedOnlyValueEdits.has(condition.id)) {
      this.excludedOnlyValueEdits.add(condition.id);
      this.filterCriteriaDataService.excludedValuesMap[condition.id] = normalizedValue;
      const included = this.splitConditionValues(
        typeof condition.value === 'string' ? condition.value : '',
      );
      const excluded = this.splitConditionValues(normalizedValue);
      this.syncOperatorForIncludedExcluded(condition, included, excluded);
      return;
    }

    condition.value = normalizedValue;
    const included = this.splitConditionValues(condition.value);
    const excluded = this.splitConditionValues(
      this.filterCriteriaDataService.excludedValuesMap[condition.id] || '',
    );
    this.syncOperatorForIncludedExcluded(condition, included, excluded);
  }

  private splitConditionValues(value: string): string[] {
    return (value || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  private syncOperatorForIncludedExcluded(
    condition: QueryCondition,
    included: string[],
    excluded: string[],
  ): void {
    const hasIncluded = included.length > 0;
    const hasExcluded = excluded.length > 0;

    if (hasIncluded && hasExcluded) {
      condition.operator = this.filterCriteriaDataService.getOperatorsLabel(
        '=,!=',
      );
      return;
    }
  }

  onFileUpload(event: Event, conditions: QueryCondition): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const data = e.target?.result;
      if (!(data instanceof ArrayBuffer) && !ArrayBuffer.isView(data)) return;
      const flatValues = await readFirstExcelColumn(data);

      const existingValues = conditions.value
        ? conditions.value
            .split(',')
            .map((v: string) => v.trim())
            .filter((v: string) => v)
        : [];
      const newValues = [...existingValues, ...flatValues];
      const uniqueValues = [...new Set(newValues)];

      conditions.value = uniqueValues.join(', ');
    };

    reader.readAsArrayBuffer(file);

    input.value = '';
  }

  updateVasLevelOptions() {
    this.filterCriteriaDataService.updateVasLevelOptions(this.elementRef);
  }

  private sortFieldsLabel(
    fields: { label: string; value: string }[],
  ): { label: string; value: string }[] {
    return [...fields].sort((left, right) =>
      left.label.localeCompare(right.label),
    );
  }

  private getFieldsForSelectedColumns(
    selectedColumns: string[],
  ): { label: string; value: string }[] {
    if (!Array.isArray(selectedColumns) || selectedColumns.length === 0) {
      return this.sortFieldsLabel(this.filterCriteriaDataService.fields);
    }

    const selectedSet = new Set(
      selectedColumns.map((column) => this.normalizeColumnName(column)),
    );
    const matchedSelected = new Set<string>();

    const fieldAliases: Record<string, string[]> = FIELD_ALIASES;

    const mappedFields = this.filterCriteriaDataService.fields.filter(
      (field) => {
        const aliases =
          fieldAliases[this.normalizeColumnName(field.value)] || [];
        const candidates = [
          field.label,
          field.value,
          this.filterCriteriaDataService.mapBackendFieldToFrontend(field.value),
          this.filterCriteriaDataService.mapBackendFieldToFrontend(field.label),
          ...aliases,
        ].map((candidate) => this.normalizeColumnName(candidate));

        const isMatch = candidates.some((candidate) =>
          selectedSet.has(candidate),
        );
        if (isMatch) {
          candidates.forEach((candidate) => {
            if (selectedSet.has(candidate)) {
              matchedSelected.add(candidate);
            }
          });
        }

        return isMatch;
      },
    );

    // Keep unmatched selected columns visible in the dropdown so users always see all selections.
    const dynamicFields = selectedColumns
      .filter(
        (column) => !matchedSelected.has(this.normalizeColumnName(column)),
      )
      .map((column) => ({ label: column, value: column }));

    const uniqueFields = new Map<string, { label: string; value: string }>();
    [...mappedFields, ...dynamicFields].forEach((field) => {
      const key = this.normalizeColumnName(field.label);
      if (!uniqueFields.has(key)) {
        uniqueFields.set(key, field);
      }
    });

    return this.sortFieldsLabel(Array.from(uniqueFields.values()));
  }

  private normalizeColumnName(value: string | null | undefined): string {
    return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  getBusinessLines(): Reference[] {
    this.filterCriteriaDataService.businessLines = BUSINESS_LINE_OPTIONS;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.filterCriteriaDataService.syncMultiselectDropdowns(this.elementRef);
    }, 50);
    return this.filterCriteriaDataService.businessLines;
  }

  getVasTypes(): { id: string; name: string }[] {
    this.filterCriteriaDataService.vasTypes = VAS_TYPE_OPTIONS;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.filterCriteriaDataService.syncMultiselectDropdowns(this.elementRef);
    }, 50);
    return this.filterCriteriaDataService.vasTypes;
  }

  getContractTypes(): string[] {
    this.filterCriteriaDataService.contract = CONTRACT_TYPE;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.filterCriteriaDataService.syncMultiselectDropdowns(this.elementRef);
    }, 50);
    return this.filterCriteriaDataService.contract;
  }

  getCountries(): Reference[] {
    this.http.getAllCountries().subscribe({
      next: (response: any[]) => {
        this.filterCriteriaDataService.countries = (response || []).filter(
          (c) => c && c.id && c.id.trim() !== '',
        );
        this.convertCountryCodesToDisplayValues();
        this.cdr.detectChanges();
        // After menu items render, sync checkbox states
        setTimeout(() => {
          this.filterCriteriaDataService.syncMultiselectDropdowns(
            this.elementRef,
          );
        }, 50);
      },
      error: (err) => console.error('Error fetching countries:', err),
    });
    return this.filterCriteriaDataService.countries;
  }

  private convertCountryCodesToDisplayValues(): void {
    for (const group of this.filterCriteriaDataService.groups) {
      for (const condition of group.conditions) {
        if (this.isCountryCondition(condition)) {
          condition.value = this.normalizeCountryValue(condition.value);
        }
      }
    }
  }

  private isCountryCondition(condition: any): boolean {
    return condition.field?.toLowerCase() === 'country' && condition.value;
  }

  private normalizeCountryValue(value: string | string[]): string[] {
    const values = typeof value === 'string' ? this.splitValues(value) : value;

    return values.map((v) => this.mapCountryCode(v));
  }

  private splitValues(value: string): string[] {
    return value.includes(',')
      ? value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      : [value.trim()];
  }

  private mapCountryCode(countryCode: string): string {
    if (countryCode.includes('(')) return countryCode;

    const country = this.filterCriteriaDataService.countries.find(
      (c) => c.id === countryCode,
    );
    if (!country) return countryCode;
    return country.name && country.name.trim() !== ''
      ? `${country.name} (${country.id})`
      : country.id;
  }

  checkForInvalidIVKs(rawValue: string, conditionId: number): boolean {
    const condId = conditionId.toString();
    if (this.filterCriteriaDataService.ivkLoadingMap[condId]) {
      return false;
    }
    const ivkMap = this.filterCriteriaDataService.ivkMap[conditionId] || [];
    const ignored = new Set(this.ignoredIvkErrorsMap[condId] || []);
    const excludedRaw =
      this.filterCriteriaDataService.excludedValuesMap[conditionId] || '';
    const matNos = [
      ...new Set([
        ...this.splitMatNos(rawValue),
        ...this.splitMatNos(excludedRaw),
      ]),
    ];
    for (const matNo of matNos) {
      if (ignored.has(matNo)) {
        continue;
      }
      if (!ivkMap.find((ivk) => ivk.id === matNo)) {
        return true; // Invalid IVK found
      }
    }
    return false;
  }

  onIVKChange(
    ivkNum: string,
    targetRow: {
      matNo: string;
      originalMatNo: string;
      ivkDescription: string;
      type: 'Included' | 'Excluded' | 'Error';
      isExcluded: boolean;
    },
  ) {
    const updatedMatNo = (ivkNum || '').trim();
    if (!updatedMatNo) {
      return;
    }

    const previousMatNo = targetRow.originalMatNo || targetRow.matNo;

    // Check for duplicate before making any changes
    if (this.ivkModalData.some((item) => item !== targetRow && item.matNo === updatedMatNo)) {
      this.notification = {
        label: 'Error',
        description: 'Duplicate entry: We already have this material number. Please enter a different one.',
        type: 'error',
      };
      this.cdr.detectChanges();
      return;
    }

    this.replaceMaterialInConditionValues(previousMatNo, updatedMatNo);

    this.filterCriteriaAPIService.getIVKDescription([updatedMatNo])
    .pipe(debounceTime(500))
    .subscribe((result: any[]) => {
      const found = (result || []).find((entry: any) => entry.id === updatedMatNo) || (result || [])[0];

      targetRow.matNo = updatedMatNo;
      targetRow.originalMatNo = updatedMatNo;
      targetRow.ivkDescription = found ? found.name : '-';
      targetRow.type = found?.name
        ? targetRow.isExcluded
          ? 'Excluded'
          : 'Included'
        : 'Error';

      if (this.activeIvkCondId) {
        const ignored = new Set(this.ignoredIvkErrorsMap[this.activeIvkCondId] || []);
        ignored.delete(previousMatNo);
        ignored.delete(updatedMatNo);
        this.ignoredIvkErrorsMap[this.activeIvkCondId] = [...ignored];
      }

      if (this.activeIvkCondId) {
        const current = [
          ...(this.filterCriteriaDataService.ivkMap[this.activeIvkCondId] || []),
        ];
        const filtered = current.filter(
          (entry: any) => entry.id !== previousMatNo && entry.id !== updatedMatNo,
        );

        if (found?.name) {
          filtered.push({ id: updatedMatNo, name: found.name });
        }

        this.filterCriteriaDataService.ivkMap[this.activeIvkCondId] = filtered;
      }

      this.sortIvkModalData();
      this.autoSelectNextErrorMaterial();

      this.cdr.detectChanges();
    });
  }

  toggleIvkErrorSelection(matNo: string, event: any): void {
    const selected = !event.target.active;
    this.ivkModalData = this.ivkModalData.map((item) => {
      if (item.matNo === matNo && item.type === 'Error') {
        return { ...item, isActive: selected };
      }
      return item;
    });
  }

  showIvkDescriptionInput(item: { ivkDescription: string }): boolean {
    return item.ivkDescription === '-';
  }

  hasIgnoredIVKs(condId: string): boolean {
    return (this.ignoredIvkErrorsMap[condId] || []).length > 0;
  }
  /**
   * Method for Ignore IVK Errors button click. It will mark the selected IVK error as ignored and update the modal data accordingly.
   */
  ignoreIvkErrors(): void {
    if (!this.activeIvkCondId) {
      return;
    }

    const selectedMatNos = this.getSelectedIvkErrorMatNos();
    if (selectedMatNos.length === 0) {
      return;
    }

    const errors = this.ivkModalData
      .filter((item) => item.type === 'Error' && selectedMatNos.includes(item.matNo))
      .map((item) => item.matNo);
    if (errors.length === 0) {
      return;
    }

    const ignored = new Set(this.ignoredIvkErrorsMap[this.activeIvkCondId] || []);
    errors.forEach((matNo) => ignored.add(matNo));
    this.ignoredIvkErrorsMap[this.activeIvkCondId] = [...ignored];

    this.ivkModalData = this.ivkModalData.map((item) => {
      if (item.type !== 'Error' || !selectedMatNos.includes(item.matNo)) {
        return item;
      }
      return {
        ...item,
        type: item.isExcluded ? 'Excluded' : 'Included',
        isActive: false,
      };
    });

    this.sortIvkModalData();
    this.autoSelectNextErrorMaterial();
    this.cdr.detectChanges();
  }

  /**
   * Method for Remove IVK Errors button click. It will remove the selected IVK error from the modal data and update the condition values accordingly.
   */
  removeSelectedIvkErrors(): void {
    if (!this.activeIvkCondId) {
      return;
    }

    const selectedMatNos = this.getSelectedIvkErrorMatNos();
    if (selectedMatNos.length === 0) {
      return;
    }

    const toRemove = this.ivkModalData
      .filter((item) => item.type === 'Error' && selectedMatNos.includes(item.matNo))
      .map((item) => item.matNo);
    if (toRemove.length === 0) {
      return;
    }

    this.ivkModalData = this.ivkModalData.filter(
      (item) => !(item.type === 'Error' && selectedMatNos.includes(item.matNo)),
    );

    const remainingErrors = new Set(
      this.ivkModalData
        .filter((item) => item.type === 'Error')
        .map((item) => item.matNo),
    );
    this.ivkModalData = this.ivkModalData.map((item) =>
      item.type === 'Error' ? { ...item, isActive: false } : item,
    );
    const nextError = [...remainingErrors]
      .sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
      )[0];
    if (nextError) {
      this.ivkModalData = this.ivkModalData.map((item) =>
        item.type === 'Error' && item.matNo === nextError
          ? { ...item, isActive: true }
          : item,
      );
    }

    this.removeMaterialIdsFromConditionValues(toRemove);
    this.sortIvkModalData();
    this.cdr.detectChanges();
  }

  private splitMatNos(raw: string | string[]): string[] {
    const source = Array.isArray(raw) ? raw.join(',') : raw || '';
    return source
      .split(/[(),&\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  private sortIvkModalData(): void {
    this.ivkModalData.sort((left, right) => {
      const rank = (type: 'Included' | 'Excluded' | 'Error') =>
        type === 'Error' ? 0 : 1;
      const byType = rank(left.type) - rank(right.type);
      if (byType !== 0) {
        return byType;
      }
      return left.matNo.localeCompare(right.matNo, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
  }

  private autoSelectNextErrorMaterial(): void {
    const nextError = this.ivkModalData.find((item) => item.type === 'Error');
    this.ivkModalData = this.ivkModalData.map((item) => {
      if (item.type !== 'Error') {
        return item;
      }
      return { ...item, isActive: !!nextError && item.matNo === nextError.matNo };
    });
  }

  hasActiveIvkErrorSelection(): boolean {
    return this.ivkModalData.some(
      (item) => item.type === 'Error' && item.isActive,
    );
  }

  private getSelectedIvkErrorMatNos(): string[] {
    return this.ivkModalData
      .filter((item) => item.type === 'Error' && item.isActive)
      .map((item) => item.matNo);
  }

  private replaceMaterialInConditionValues(
    previousMatNo: string,
    updatedMatNo: string,
  ): void {
    if (!this.activeIvkCondId) {
      return;
    }

    const condIdNum = Number(this.activeIvkCondId);
    const condition = this.findConditionById(condIdNum);
    if (condition && typeof condition.value === 'string') {
      const replaced = this.splitMatNos(condition.value).map((matNo) =>
        matNo === previousMatNo ? updatedMatNo : matNo,
      );
      condition.value = replaced.join(',');
    }

    const excludedRaw =
      this.filterCriteriaDataService.excludedValuesMap[condIdNum] || '';
    const excludedReplaced = this.splitMatNos(excludedRaw).map((matNo) =>
      matNo === previousMatNo ? updatedMatNo : matNo,
    );
    this.filterCriteriaDataService.excludedValuesMap[condIdNum] =
      excludedReplaced.join(',');
  }

  private removeMaterialIdsFromConditionValues(matNos: string[]): void {
    if (!this.activeIvkCondId || matNos.length === 0) {
      return;
    }

    const removeSet = new Set(matNos);
    const condIdNum = Number(this.activeIvkCondId);

    const condition = this.findConditionById(condIdNum);
    if (condition && typeof condition.value === 'string') {
      const updatedIncluded = this.splitMatNos(condition.value).filter(
        (matNo) => !removeSet.has(matNo),
      );
      condition.value = updatedIncluded.join(',');
    }

    const excludedRaw =
      this.filterCriteriaDataService.excludedValuesMap[condIdNum] || '';
    const updatedExcluded = this.splitMatNos(excludedRaw).filter(
      (matNo) => !removeSet.has(matNo),
    );
    this.filterCriteriaDataService.excludedValuesMap[condIdNum] =
      updatedExcluded.join(',');

    const ignored = new Set(this.ignoredIvkErrorsMap[this.activeIvkCondId] || []);
    matNos.forEach((matNo) => ignored.delete(matNo));
    this.ignoredIvkErrorsMap[this.activeIvkCondId] = [...ignored];
  }

  private findConditionById(conditionId: number): QueryCondition | null {
    for (const group of this.filterCriteriaDataService.groups) {
      const found = group.conditions.find((condition) => condition.id === conditionId);
      if (found) {
        return found;
      }
    }
    return null;
  } 

  ngOnDestroy(): void {
    if (this.selectedColumnsSubscription) {
      this.selectedColumnsSubscription.unsubscribe();
    }
  }
}
